import * as Sentry from "@sentry/react";
import { localGetData } from "./utils";
import { sessionRequestHeaders } from "./requestHeaders";

const LOG_OUT_MUTATION = `
  mutation logOut($input: LogOutInput!) {
    logOut(input: $input) { success errors }
  }
`;

const CLIENT_REASONS = [
  "USER_REQUESTED",
  "CLIENT_INACTIVITY_TIMEOUT",
  "CLIENT_SESSION_EXPIRED",
  "CLIENT_UNAUTHORIZED",
  "CLIENT_STORAGE_RESET",
];

export async function revokeSession(token, reason, {
  apiHost = import.meta.env.VITE_API_HOST,
  reportErrors = true,
} = {}) {
  if (!token) return;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 10000);
  try {
    // Logout must bypass Apollo's offline queue and use the token captured before
    // local cleanup. keepalive allows it to finish when we navigate to /mp.
    const response = await fetch(apiHost, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...sessionRequestHeaders(token),
        timestamp: String(Math.floor(Date.now() / 1000)),
      },
      body: JSON.stringify({
        operationName: "logOut",
        query: LOG_OUT_MUTATION,
        variables: { input: { reason } },
      }),
      keepalive: true,
      signal: controller.signal,
    });
    const result = await response.json();
    const errors = [
      ...(Array.isArray(result?.errors) ? result.errors : []),
      ...(Array.isArray(result?.data?.logOut?.errors) ? result.data.logOut.errors : []),
    ];
    if (response.ok && result?.data?.logOut?.success && errors.length === 0) return;
    // A token that can no longer identify a user is already unusable. The API
    // audits that rejection. Suppress it only when every error is expected;
    // partial success or a mixed response must not hide a cleanup failure.
    if (errors.length > 0 && errors.every(error =>
      (error?.extensions?.code || error?.message || error) === "AUTHENTICATION_REQUIRED"
    )) return;
    throw new Error(`Logout mutation failed (HTTP ${response.status})`);
  } catch (error) {
    // Switching a development endpoint should not produce error reports when
    // the previous API is offline or does not support logout yet.
    if (!reportErrors) return;
    console.error("Could not clear the server session during logout.", error);
    Sentry.captureException(error, { tags: { operation: "logOut", reason } });
  } finally {
    clearTimeout(timeout);
  }
}

export function logOutSession(reason = "USER_REQUESTED", expectedToken = localGetData("mp_token") || null, options = {}) {
  const token = localGetData("mp_token") || null;
  // An old request or tab must not clear a login that replaced its own token.
  if (expectedToken !== token) return false;

  const logoutReason = CLIENT_REASONS.includes(reason) ? reason : "USER_REQUESTED";
  // A React click event is a supported manual-logout caller. Other unexpected
  // values need a diagnostic, but may contain sensitive data, so do not log them.
  if (!CLIENT_REASONS.includes(reason) && reason?.type !== "click") {
    console.warn("Unknown logout reason; using USER_REQUESTED.");
  }
  window.localStorage.removeItem("mp_user");
  window.localStorage.removeItem("mp_token");
  void revokeSession(token, logoutReason, options);
  return true;
}
